import type { RefObject } from 'react';

import { useEffect, useState } from 'react';

import type { ElementSize } from '.';

/**
 * React hook to observe element size and optionally DOMRect.
 * @param ref - Ref to the element.
 * @param returnDOMRect - If true, also returns DOMRect.
 * @returns [size, domRect]
 * @example
 * const [size, rect] = useElementData(ref, true);
 */
export function useElementData<T extends HTMLElement = HTMLDivElement>(target: RefObject<T | null>, returnDOMRect = false): [ElementSize, DOMRect | undefined] {
  const [size, setSize] = useState<ElementSize>({
    width: 0,
    height: 0,
  });
  const [DOMRect, setDOMRect] = useState<DOMRect>();

  useEffect(() => {
    const targetElement = target.current;
    if (targetElement) {
      const resizeObserver = new ResizeObserver(() => {
        setSize({
          width: targetElement.offsetWidth,
          height: targetElement.offsetHeight,
        });
        if (returnDOMRect) {
          setDOMRect(targetElement.getBoundingClientRect());
        }
      });

      resizeObserver.observe(targetElement);

      return () => {
        resizeObserver.unobserve(targetElement);
      };
    }
  }, [target, returnDOMRect]);

  return [size, returnDOMRect ? DOMRect : undefined];
}
